import type { MemberDataFirestoreRecord } from "@albertine/types";
import type {
    Conversation,
    ConversationUnreadMessages,
} from "../../common/types/Conversation";
import type { Request } from "../../common/types/Request";
import { formatMemberName } from "../../common/utils/name.util";
import type { MessageType } from "./ChatContext";

type OnboardingGreeting = {
    greetingsName?: string | null;
};

export function formatReplyPlaceHolderText(
    messageType: MessageType,
    onboarding: OnboardingGreeting | undefined,
) {
    if (messageType === "internal_note") {
        return "Write your internal note here...";
    }
    if (onboarding?.greetingsName) {
        return `Preferred greeting: ${onboarding.greetingsName}`;
    }

    return "Preferred greeting: n/a";
}

export function formatReplyToText(
    request: Request | undefined,
    member: MemberDataFirestoreRecord | undefined,
    onboarding: OnboardingGreeting | undefined,
) {
    if (request) {
        return `Reply to #${request.ticketNumber} ${request.title}`;
    }

    if (onboarding?.greetingsName) {
        return `Reply to ${onboarding.greetingsName}`;
    }

    if (member) {
        return `Reply to ${formatMemberName(member)}`;
    }

    return "Reply";
}

export function canMarkMessagesAsRead(
    activeRequest: Request | undefined,
    unreadMessagesByAgent: ConversationUnreadMessages | undefined | null,
) {
    if (!unreadMessagesByAgent) {
        return false;
    }

    if (activeRequest) {
        return (
            unreadMessagesByAgent.filter((it) => it.request === activeRequest.id)
                .length !== 0
        );
    }

    return unreadMessagesByAgent.filter((it) => !it.request).length !== 0;
}

export function unreadMessagesPerThread(
    requestThread: Request | undefined,
    conversation: Conversation,
) {
    const unreadTotal = conversation.unreadMessagesCountByAgent ?? 0;
    const unreadInRequestThread = requestThread
        ? (conversation.unreadMessagesByAgent?.filter(
              (it) => it.request === requestThread.id,
          ).length ?? 0)
        : 0;

    return {
        unreadTotal,
        unreadInRequestThread,
    };
}

type SemVer = {
    major: number;
    minor: number;
    patch: number | undefined;
};

// Parse a semver string into its components
function parseSemVer(version: string): SemVer {
    const [major, minor, patch] = version.split(".").map((num) => parseInt(num, 10));
    return { major, minor, patch };
}

export function testAppVersionIsAtLeast(p: {
    appVersion: string | null | undefined;
    minVersion: string;
}): boolean {
    const { appVersion, minVersion } = p;
    if (!appVersion) {
        return false;
    }

    const semver = parseSemVer(appVersion);
    const minSemver = parseSemVer(minVersion);

    if (semver.major < minSemver.major) {
        return false;
    }
    if (semver.major > minSemver.major) {
        return true;
    }
    if (semver.minor < minSemver.minor) {
        return false;
    }
    if (semver.patch === undefined && minSemver.patch !== undefined) {
        return false;
    }
    if (semver.patch === undefined || minSemver.patch === undefined) {
        return true;
    }
    return semver.patch >= minSemver.patch;
}
